import { apply, give, has, take } from '../core/effects';
import type { ItemKey } from '../core/state';
import { S } from '../core/game';
import type { GameState } from '../core/state';
import { m } from '../i18n';
import { npcName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { say, type Choice } from '../ui/dialog';
import type { NpcId } from '../world/npcs';
import { advance, canStart, completeQuest, isActive, isDone, questDef, QUESTS, startQuest, step, type QuestId } from './quests';

/**
 * The story layer: chapters that group the quests, the dialogue options that
 * existing characters gain for the cross-map quests (offers, errands, hand-ins),
 * and the "!" / "?" markers drawn over their heads.
 */

export type ChapterId = 'school' | 'town' | 'taipei' | 'hualien';

export interface Chapter {
  id: ChapterId;
  icon: string;
  quests: QuestId[];
}

export const CHAPTERS: readonly Chapter[] = [
  { id: 'school', icon: '🏫', quests: ['atlas', 'riceBall', 'laps', 'study'] },
  { id: 'town', icon: '🏘️', quests: ['kitten', 'kite', 'xiaoyuPicnic', 'letter', 'clovers'] },
  { id: 'taipei', icon: '🏙️', quests: ['postcards', 'pineapple', 'elephant'] },
  { id: 'hualien', icon: '🏖️', quests: ['mochi', 'pebble'] },
];

export function chapterOf(id: QuestId): Chapter {
  return CHAPTERS.find((c) => c.quests.includes(id)) ?? CHAPTERS[0];
}

export function chapterProgress(c: Chapter, s: GameState = S()): { done: number; total: number } {
  return { done: c.quests.filter((q) => s.quests[q]?.done).length, total: c.quests.length };
}

/** Which characters hand out the postcards, and the bit each one sets in the quest data. */
export const POSTCARD_GIVERS: readonly NpcId[] = ['guide', 'xm_volunteer', 'ck_guide'];

/** A quest's giver (the character you talk to first), for markers and the journal. */
export function giverOf(id: QuestId, s: GameState = S()): NpcId | null {
  const t = questDef(id).target(0, s);
  return t && t.kind === 'npc' ? t.id : null;
}

/** "!" when the character offers a quest, "?" when an active quest points at them. */
export function npcMarker(id: NpcId, s: GameState = S()): '!' | '?' | null {
  for (const q of QUESTS) {
    if (isActive(q.id)) {
      const t = q.target(step(q.id), s);
      if (t && t.kind === 'npc' && t.id === id) return '?';
    }
  }
  for (const q of QUESTS) if (canStart(q.id) && giverOf(q.id, s) === id) return '!';
  return null;
}

const price = (coins: number, ok: () => void, poor: string, who: string, icon: string) => () => {
  if (S().coins < coins) return say({ who, icon, text: poor });
  apply({ coins: -coins });
  sfx('pop');
  ok();
};

/** Extra dialogue options a character gets from the story quests. */
export function storyOptions(id: string): Choice[] {
  const t = m().story.quests;
  const who = npcName(id);
  const out: Choice[] = [];
  const offer = (q: QuestId, text: string, accept: string, icon: string) => {
    if (!canStart(q) || giverOf(q) !== id) return;
    out.push({ text: `❗ ${m().quests.list[q].title}`, run: () => say({ who, icon, text, choices: [{ text: accept, run: () => startQuest(q, 0) }, { text: m().common.no }] }) });
  };
  const handIn = (q: QuestId, item: ItemKey, n: number, label: string, thanks: string, icon: string) => {
    if (!(isActive(q) && step(q) === 2 && giverOf(q) === id && has(item, n))) return;
    out.push({
      text: `🎁 ${label}`,
      run: () => {
        take(item, n);
        completeQuest(q);
        say({ who, icon, text: thanks });
      },
    });
  };

  // --- Taipei chapter ---------------------------------------------------------------------
  offer('postcards', t.postcards.offer, t.postcards.accept, '👵');
  if (isActive('postcards') && step('postcards') === 1) {
    const i = POSTCARD_GIVERS.indexOf(id as NpcId);
    const bit = 1 << i;
    if (i >= 0 && !((S().quests.postcards!.data ?? 0) & bit)) {
      const line = t.postcards.collect[id as keyof typeof t.postcards.collect];
      out.push({
        text: `🖼️ ${t.postcards.ask}`,
        run: () => {
          const q = S().quests.postcards!;
          q.data = (q.data ?? 0) | bit;
          give('postcard');
          say({ who, icon: '🖼️', text: line });
          if (q.data === (1 << POSTCARD_GIVERS.length) - 1) advance('postcards', 2);
        },
      });
    }
  }
  handIn('postcards', 'postcard', 3, t.postcards.give, t.postcards.thanks, '👵');

  offer('pineapple', t.pineapple.offer, t.pineapple.accept, '👩');
  if (isActive('pineapple') && step('pineapple') === 1 && id === 'xy_clerk')
    out.push({ text: `🍍 ${t.pineapple.buy}`, run: price(60, () => (give('pineappleCake'), advance('pineapple', 2), say({ who, icon: '🍍', text: t.pineapple.bought })), t.pineapple.poor, who, '🍍') });
  handIn('pineapple', 'pineappleCake', 1, t.pineapple.give, t.pineapple.thanks, '👩');

  offer('elephant', t.elephant.offer, t.elephant.accept, '👧');
  if (isActive('elephant') && step('elephant') === 1 && id === 'xy_hiker')
    out.push({ text: `📸 ${t.elephant.ask}`, run: () => (sfx('pop'), give('photo'), advance('elephant', 2), say({ who, icon: '📸', text: t.elephant.shot })) });
  handIn('elephant', 'photo', 1, t.elephant.give, t.elephant.thanks, '👧');

  // --- Hualien chapter --------------------------------------------------------------------
  offer('mochi', t.mochi.offer, t.mochi.accept, '👦');
  if (isActive('mochi') && step('mochi') === 1 && id === 'tk_mochiAuntie')
    out.push({ text: `🍡 ${t.mochi.buy}`, run: price(40, () => (give('mochi'), advance('mochi', 2), say({ who, icon: '🍡', text: t.mochi.bought })), t.mochi.poor, who, '🍡') });
  handIn('mochi', 'mochi', 1, t.mochi.give, t.mochi.thanks, '👦');

  offer('pebble', t.pebble.offer, t.pebble.accept, '👧');
  if (isActive('pebble') && step('pebble') === 1 && id === 'tk_fisher')
    out.push({ text: `🪨 ${t.pebble.ask}`, run: () => (sfx('pickup'), give('pebble'), advance('pebble', 2), say({ who, icon: '🪨', text: t.pebble.found })) });
  handIn('pebble', 'pebble', 1, t.pebble.give, t.pebble.thanks, '👧');

  return out;
}

export { isDone };
